<script>
import { defineComponent, watch, onMounted, ref, computed, nextTick, inject } from 'vue'
import IconButton from '@kit/components/IconButton.vue'
import { bodyScrollOn, bodyScrollOff } from '@kit/utils/Scroller'
import { inBrowser } from '@kit/utils/EnvironmentHelper'
import Portal from '@kit/components/Portal.vue'
import { useStore } from 'vuex'
import { mergeClassesTheme, mergeStylesTheme } from '@kit/utils/Formats'
import { actionSetOA2T, actionOA2T, animate } from '@kit/utils/Animate'
import Config from '@kit/utils/Config'
import '@kit/assets/css/superbox-core-x.css'

/**
 
Modal-style gallery.

 theme:

 {
    //mergeable classes for the background
    backgroundClasses
    
    //mergeable classes for the thumnail highlight
    highlightClasses

    //the theme for the close button
    closeButton: roundCloseButton,

    //the theme for the right button
    rightButton

    //the theme for the left button
    leftButton

    //do we want to suspend scrolling when the gallery opens
    suspendScrolling: true
 }

 galleryData. The data for the gallery. has this kind of format:
 {
 "items": [
        {
          "url": "myImage.jpn",
          "type": "image",
          "alt": "picture of admin site"
        },
        ...
      ]
  }

 **/

const defaultConfig = new Config({
  suspendScrolling: true
})


export default defineComponent({
  name: "Gallery2",
  props: {
    "modelValue": { required:true, default:false },
    "theme": { required:true },
    "index": { required:false, default: 0},
    "galleryData": { required:true },

    "backgroundClasses": {required: false },
    "suspendScrolling": { required: false },
    "highlightClasses": { required: false}

  },
  setup(props, context) {

    const store = useStore()
    const inMobile = computed(() => store.state.inMobile)
    const doTeleport = ref(false)
    const currentPhotoIdx = ref(0)
    const imageElement = ref(null)
    const thumbnailPanel = ref(null)
    const photoBox = ref(null)

    const themer = inject("themer")
    const { getProp } = themer({props, defaultConfig })

    //The action set for the one-at-a-time actions
    const mainPhotoActionSet = actionSetOA2T([
      {
        name:"showPhoto",
        async action(newPhotoData, prevOrNext, photoEl) {

          photoEl.style.opacity = 0
          photoEl.setAttribute("src", newPhotoData.url)
          photoEl.setAttribute("alt", newPhotoData.alt)
          photoEl.style.transform = prevOrNext ? "translateX(30px)" : "translateX(-30px)"

          await animate({
            targets:photoEl,
            opacity:1,
            translateX:0,
            duration:500,
            easing:"easeOutQuad"
          })

        }
      }
    ])

    const panelActionSet = actionSetOA2T([
      {
        name: "doScroll",
        async action(tPanel, pBox) {

          //need this or the query-selector will get an out-of-date one
          await nextTick()
          const active = tPanel.querySelector(".active-thumbnail")

          if(active) {
            
            const selectedThumbnail = active//.parentNode
            const panelBounds = selectedThumbnail.parentNode.getBoundingClientRect()
            const photoBounds = pBox.getBoundingClientRect()
            const thumbnailBounds = selectedThumbnail.getBoundingClientRect()
            
            let goScroll = false
            let offstageLeft = false
            const currentScrollLeft = tPanel.scrollLeft
            
            const rX = thumbnailBounds.right - panelBounds.x
            const lX = thumbnailBounds.x - panelBounds.x

            if(rX > photoBounds.width) {
              goScroll = true
            } else 
            if(lX < 0) {
              offstageLeft = true 
              goScroll = true
            }

            if(goScroll) {
              await animate({
                targets: tPanel,
                scrollLeft: offstageLeft ? currentScrollLeft-200 : currentScrollLeft+200,
                duration:500,
                easing: "easeOutQuad"
              })          
            }

          }  
        }
      }

    ])


    //The usual prev-next mechanics
    const prevClicked = () => {
      if(hasPrev.value) {
        currentPhotoIdx.value--
      }
    }
    const nextClicked = () => {
      if(hasNext.value) {
        currentPhotoIdx.value++
      }
    }
    const hasPrev = computed(() => {
      if(photos.value) {
        return (currentPhotoIdx.value > 0)
      }
      return false
    })
    const hasNext = computed(() => {
      if(photos.value) {
        return (currentPhotoIdx.value < photos.value.length-1)
      } 
      return false
    })

    //on-mount. See the notes in the Modal.vue component for the doTeleport thing
    onMounted(() => {
      doTeleport.value = true

      if(!props.theme) {
        throw new Error("Gallery2.vue: expecting a theme.")
      }
    })

    //when the gallery opens, record the data.
    const galleryOpen = () => {

      //set the current index to whatever came through from the optional property
      currentPhotoIdx.value = props.index 

      //if it's 0, then that means that there's no change to the currentPhotoIdx 
      //so the watcher won't fire. So open the photo manually.
      if(props.index == 0) {
        showPhoto(true)
      }
    }

    //If a photo was selected, change the index
    const photoSelected = (e) => {
      const { target } = e 
      if(target.getAttribute("data-isphoto") == "true") {
        const idx = parseInt(target.getAttribute("data-idx"))
        currentPhotoIdx.value = idx
      }
    }

    //The current photo
    const currentDisplayPhoto = computed(() => {
      const d = props.galleryData
      if(!d) {
        return null
      }
      const idx = currentPhotoIdx.value 
      const items = d.items 
        if(items && items.length > idx) {
          return items[idx]
        }
      return null
    })

    //The photos
    const photos = computed(() => {
      const d = props.galleryData

      if(!d) {
        return null
      }
      return d.items || null
    })

    const closeGallery = (e) => {
      //close the window by emitting this event, and this is standard mechanics for the 
      //v-model directive. It's a little confusing IMO but that's how it works.
      context.emit('update:modelValue', false);
    }

    //Perform body scroll mechanics. These are designed to override the mechanics 
    //of the google cse ui that does it's own thing.
    watch(() => props.modelValue, (newVal, _oldVal) => {

      galleryOpen()

      //don't bother with changing the scrolling mechanics if 
      //we're in the mobile side-drawer.
      if(getProp('suspendScrolling')) {
        if(newVal) {
          bodyScrollOff({ saveScroll:true, correctRight:true })
        } else {
          bodyScrollOn()
        }
      }
    })

    //Show the photo and do the scrolling for the thumbnails.
    const showPhoto = (forwardOrBackward) => {
      actionOA2T(mainPhotoActionSet, "showPhoto", [currentDisplayPhoto.value, forwardOrBackward, imageElement.value])
      
      const tPanel = thumbnailPanel.value
      const pBox = photoBox.value
      if(tPanel && pBox) {
        actionOA2T(panelActionSet, "doScroll", [tPanel, pBox])
      }
    }

    //Any time the index changes, show a new photo
    watch(currentPhotoIdx, (newVal, oldVal) => {
      if(newVal >= oldVal) {
        showPhoto(true)
      } else {
        showPhoto(false)
      }
    })

    //merge the classes with the theme classes. Note that we do not allow overrides of the hidden class.
    const fixedContainerMergedClasses = computed(() => {
      const hidden = props.modelValue ? "" : "gallery2-hidden "
      const classes = `${hidden}${mergeClassesTheme('sb sb-h sb-content-space-between gallery2-window', getProp('galleryFixedContainerClasses'))}`
      return classes
    })


    return { closeGallery, inBrowser,
      doTeleport, mergeClassesTheme,
      inMobile, fixedContainerMergedClasses,
      mergeStylesTheme, currentPhotoIdx,
      currentDisplayPhoto, photos, imageElement,
      hasPrev, hasNext,
      prevClicked, nextClicked,
      photoSelected, 
      thumbnailPanel, photoBox,
      getProp
    }

  },
  components: {
    Portal,
    IconButton
  }
});
</script>

<style scoped>

  /* .active-thumbnail::before {
    content:"";
    position:absolute;
    top:0px; bottom:0px; left:0px; right:0px;
    border:2px solid #EEEEEE;
  } */
  .close-button {
    position:absolute; 
    top:0px; 
    left:auto; 
    right:0px;
  }

  .gallery2-background {
    position:fixed; 
    top:0px; 
    left:0px; 
    width:100vw; 
    height:100vh; 
    z-index:1001;
    background:black;
    opacity: 0.8; 
  }

  .gallery2-window {
    position:fixed; 
    z-index:1001; 
    height:100vh;
    width: 100vw;
    top: 0px;
    left: 0px;
  }

  .gallery2-hidden {
    display:none;
  }

  .thumbnail-panel {
    -ms-overflow-style: none;  /* Internet Explorer 10+ */
    scrollbar-width: none;  /* Firefox */
  }
  .thumbnail-panel::-webkit-scrollbar { 
      display: none;  /* Safari and Chrome */
  }

</style>

<template>
    
  <Teleport v-if="doTeleport" to="#teleport-target">
    <div v-if="modelValue" 
      :class="mergeClassesTheme('gallery2-background', getProp('backgroundClasses'))" 
      :style="mergeStylesTheme('',getProp('backgroundStyles'))" 
    ></div>
    <div 
      :class="fixedContainerMergedClasses" 
      :style="mergeStylesTheme('', getProp('galleryFixedContainerStyles'))">
        <div class="sb sb-explicit sb-v sb-align-cv" style="width:60px;">
          <IconButton @buttonClick="prevClicked" :active="hasPrev" :theme="getProp('leftButton','theme')"/>
        </div>
        <!--Need this extra wrapper so that the alignment class works -->
        <div class="sb sb-h sb-explicit sb-greedy-1" style="width:50%;">
          <div class="sb sb-v sb-content-strict-height sb-greedy sb-greedy-1">

            <!-- The selected photo -->
            <div class="sb sb-greedy" ref="photoBox" style="overflow:hidden;">
              <img src="" ref="imageElement" style="object-fit:contain; width:100%; height: 100%;">
            </div>

            <!-- The available photos in the gallery -->
            <!-- A possible style for explicitly-widthed thumbnails -->
            <!-- <div v-if="photos" ref="thumbnailPanel" class="sb sb-h sb-explicit sb-g20 thumbnail-panel" @click="photoSelected" style="height:100px; overflow-x:auto;">
              <div v-for="(photo, idx) in photos" class="sb sb-explicit" style="width:60px;">
                <div :Class="(idx == currentPhotoIdx) ? 'active-thumbnail sb sb-spread' : 'sb sb-spread'">
                  <img :src="photo.url" data-isphoto="true" :data-idx="idx" :alt="`thumbnail for ${photo.alt}`" style="object-fit:contain; width:100%; height: 100%;">
                </div>
              </div>
            </div> -->

            <div v-if="photos" ref="thumbnailPanel" class="sb sb-h sb-explicit sb-align-ch sb-g20 thumbnail-panel" @click="photoSelected" style="height:100px; overflow-x:auto;">
              <div v-for="(photo, idx) in photos" :class="(idx == currentPhotoIdx) ?  mergeClassesTheme('sb active-thumbnail', getProp('highlightClasses')) : 'sb'">
                <img 
                  :src="photo.url" data-isphoto="true" 
                  :data-idx="idx" :alt="`thumbnail for ${photo.alt}`" 
                  style="height: 100%; width:auto; max-width:none;">  
              </div>
            </div>

          </div>
        </div>
        <div class="sb sb-explicit sb-v sb-align-cv" style="width:60px;">
          <IconButton @buttonClick="nextClicked" :active="hasNext" :theme="getProp('rightButton','theme')"/>
        </div>
        <div class="sb sb-abox" style="left:auto; right:5px; top:5px">
          <IconButton @buttonClick="closeGallery" :theme="getProp('closeButton','theme')"/>
        </div>

        <!-- TODO: could use this to put optional semantic markup in the gallery -->
        <div style="display:none;">
          <slot></slot>
        </div>
    </div>
  </Teleport>

</template>